import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/components/admin/ui";
import { api, formatApiError } from "@/lib/api";
import { EXPENSE_CATEGORIES, INCOME_CATEGORIES, todayISO } from "@/lib/format";
import { useAdminTrips } from "@/hooks/queries";

const TypeToggle = ({ value, onChange }) => (
  <div className="grid grid-cols-2 gap-2">
    {[["pemasukan", "Pemasukan", "bg-emerald-500/15 border-emerald-400/40 text-emerald-200"], ["pengeluaran", "Pengeluaran", "bg-rose-500/15 border-rose-400/40 text-rose-200"]].map(([key, label, active]) => (
      <button key={key} type="button" onClick={() => onChange(key)} data-testid={`tx-type-${key}`}
        className={`h-11 rounded-xl border text-sm transition-colors ${value === key ? active : "border-white/10 text-neutral-400 hover:text-white"}`}>
        {label}
      </button>
    ))}
  </div>
);

export const TransactionDialog = ({ tx, defaultType = "pengeluaran", onClose, onSaved }) => {
  const isEdit = !!tx?.id;
  const { data: trips = [] } = useAdminTrips();
  const [form, setForm] = useState(() => ({
    type: tx?.type || defaultType, category: tx?.category || "", amount: tx?.amount ?? "", date: tx?.date || todayISO(),
    description: tx?.description || "", trip_id: tx?.trip_id || "none",
  }));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const categories = form.type === "pemasukan" ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;
  const options = form.category && !categories.includes(form.category) ? [form.category, ...categories] : categories;

  const save = useMutation({
    mutationFn: (payload) => (isEdit ? api.put(`/admin/transactions/${tx.id}`, payload) : api.post("/admin/transactions", payload)),
    onSuccess: () => { toast.success("Transaksi disimpan"); onSaved(); onClose(); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const submit = (e) => {
    e.preventDefault();
    if (!form.category) return toast.error("Pilih kategori");
    save.mutate({ ...form, amount: Number(form.amount) || 0, trip_id: form.trip_id === "none" ? null : form.trip_id });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg border-white/10 bg-ink-900" data-testid="tx-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{isEdit ? "Edit transaksi" : "Catat transaksi"}</DialogTitle>
          <DialogDescription>Pemasukan dan pengeluaran operasional trip.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <TypeToggle value={form.type} onChange={(v) => setForm((f) => ({ ...f, type: v, category: "" }))} />
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Kategori">
              <Select value={form.category || undefined} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="tx-category"><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
                <SelectContent>{options.map((c) => <SelectItem key={c} value={c} data-testid={`tx-category-option-${c.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Jumlah (Rp)"><Input type="number" min={0} required value={form.amount} onChange={(e) => set("amount", e.target.value)} data-testid="tx-amount" /></Field>
            <Field label="Tanggal"><Input type="date" required value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="tx-date" /></Field>
            <Field label="Trip (opsional)">
              <Select value={form.trip_id} onValueChange={(v) => set("trip_id", v)}>
                <SelectTrigger data-testid="tx-trip"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Tidak terkait trip</SelectItem>
                  {trips.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Keterangan"><Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="tx-description" /></Field>
          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" onClick={onClose} data-testid="tx-cancel">Batal</Button>
            <Button type="submit" disabled={save.isPending} data-testid="tx-submit">{save.isPending && <Loader2 className="animate-spin" />} Simpan</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
